import { useCallback, useEffect, useState } from 'react';
import { isRenewalReminderDue } from '@app/common';
import type { UserSubscription } from '@app/common';
import { renewalReminderStorageKey } from '../constants/subscription';
import { useAuth } from '../context/AuthContext';

/**
 * localStorage throws in private-mode Safari and when storage is disabled. A
 * reminder that cannot remember its dismissal is still better than a dashboard
 * that fails to render, so both accessors swallow it — the cost is that the
 * prompt reappears on the next visit.
 */
function readDismissedAt(userId: string): string | null {
  try {
    return localStorage.getItem(renewalReminderStorageKey(userId));
  } catch {
    return null;
  }
}

function writeDismissedAt(userId: string, iso: string): void {
  try {
    localStorage.setItem(renewalReminderStorageKey(userId), iso);
  } catch {
    // Dismissal lasts for this page view only.
  }
}

function clearDismissedAt(userId: string): void {
  try {
    localStorage.removeItem(renewalReminderStorageKey(userId));
  } catch {
    // Nothing to clean up if storage is unavailable — nothing was written either.
  }
}

/**
 * Decides whether to prompt an expired subscriber to subscribe again.
 *
 * The banner shows the moment a subscription is found expired, and every
 * `RENEWAL_REMINDER_INTERVAL_DAYS` after each dismissal for as long as it stays
 * expired. Dismissal is per browser: there is no column behind it, so a teacher
 * who dismisses on a laptop is prompted again on their phone. That is the
 * accepted cost of keeping this change free of a schema migration.
 */
export function useRenewalReminder(subscription: UserSubscription | null): {
  visible: boolean;
  dismiss: () => void;
} {
  const { user } = useAuth();
  const userId = user?.id ?? null;

  // Read on first render rather than in an effect alone, so an already-dismissed
  // reminder never flashes on screen before the effect can hide it.
  const [dismissedAt, setDismissedAt] = useState<string | null>(() =>
    userId ? readDismissedAt(userId) : null
  );

  const status = subscription?.status;

  useEffect(() => {
    if (!userId) {
      setDismissedAt(null);
      return;
    }

    // A subscription that is no longer expired resets the cycle: the *next* expiry
    // has to prompt immediately rather than inherit a dismissal from the last one.
    if (status && status !== 'expired') {
      clearDismissedAt(userId);
      setDismissedAt(null);
      return;
    }

    setDismissedAt(readDismissedAt(userId));
  }, [userId, status]);

  const dismiss = useCallback(() => {
    if (!userId) return;
    const now = new Date().toISOString();
    writeDismissedAt(userId, now);
    setDismissedAt(now);
  }, [userId]);

  // No user or no subscription loaded yet means nothing is known to be expired.
  const visible =
    Boolean(userId) && status !== undefined && isRenewalReminderDue(status, dismissedAt);

  return { visible, dismiss };
}
